import React, { useState } from 'react';
import { Users, MessageSquare, FileText, Calendar, Bell, Search, Plus, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';

const CollaborationHub = () => {
  const [activeTab, setActiveTab] = useState('team-collaboration');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Mock data for team collaboration
  const teamMembers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'Contract Manager',
      department: 'Legal',
      status: 'Online',
      lastActive: '2 minutes ago',
      projects: 5,
      avatar: 'SJ'
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Legal Counsel',
      department: 'Legal',
      status: 'Away',
      lastActive: '15 minutes ago',
      projects: 3,
      avatar: 'MC'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'Finance Analyst',
      department: 'Finance',
      status: 'Online',
      lastActive: '1 minute ago',
      projects: 7,
      avatar: 'ER'
    },
    {
      id: 4,
      name: 'David Kim',
      role: 'Procurement Manager',
      department: 'Procurement',
      status: 'Offline',
      lastActive: '2 hours ago',
      projects: 4,
      avatar: 'DK'
    }
  ];

  // Mock data for document sharing
  const sharedDocuments = [
    {
      id: 1,
      name: 'Q4 Contract Review',
      type: 'PDF',
      size: '2.4 MB',
      sharedBy: 'Sarah Johnson',
      sharedDate: '2024-01-15',
      accessLevel: 'Team',
      downloads: 12,
      status: 'Active'
    },
    {
      id: 2,
      name: 'Vendor Agreement Template',
      type: 'DOCX',
      size: '1.8 MB',
      sharedBy: 'Michael Chen',
      sharedDate: '2024-01-14',
      accessLevel: 'Public',
      downloads: 8,
      status: 'Active'
    },
    {
      id: 3,
      name: 'Budget Analysis 2024',
      type: 'XLSX',
      size: '3.2 MB',
      sharedBy: 'Emily Rodriguez',
      sharedDate: '2024-01-13',
      accessLevel: 'Restricted',
      downloads: 5,
      status: 'Active'
    }
  ];

  // Mock data for communication
  const communications = [
    {
      id: 1,
      type: 'Message',
      from: 'Sarah Johnson',
      to: 'Team',
      subject: 'Contract Review Meeting',
      content: 'Please review the attached contract before our meeting tomorrow.',
      timestamp: '2024-01-15 10:30 AM',
      status: 'Unread',
      priority: 'High'
    },
    {
      id: 2,
      type: 'Announcement',
      from: 'System',
      to: 'All Users',
      subject: 'System Maintenance',
      content: 'Scheduled maintenance will occur tonight from 11 PM to 1 AM.',
      timestamp: '2024-01-15 09:15 AM',
      status: 'Read',
      priority: 'Medium'
    },
    {
      id: 3,
      type: 'Notification',
      from: 'Michael Chen',
      to: 'Legal Team',
      subject: 'New Contract Template',
      content: 'Updated contract template is now available in the shared folder.',
      timestamp: '2024-01-15 08:45 AM',
      status: 'Read',
      priority: 'Low'
    }
  ];

  // Mock data for project collaboration
  const projects = [
    {
      id: 1,
      name: 'Q4 Contract Renewals',
      description: 'Review and renew all expiring contracts',
      team: ['Sarah Johnson', 'Michael Chen', 'Emily Rodriguez'],
      progress: 75,
      deadline: '2024-01-31',
      status: 'In Progress',
      tasks: 12,
      completed: 9
    },
    {
      id: 2,
      name: 'Vendor Onboarding',
      description: 'Onboard new vendors and update contracts',
      team: ['David Kim', 'Sarah Johnson'],
      progress: 45,
      deadline: '2024-02-15',
      status: 'In Progress',
      tasks: 8,
      completed: 3
    },
    {
      id: 3,
      name: 'Compliance Audit',
      description: 'Annual compliance audit and documentation',
      team: ['Michael Chen', 'Emily Rodriguez'],
      progress: 90,
      deadline: '2024-01-25',
      status: 'Near Completion',
      tasks: 15,
      completed: 14
    }
  ];

  const tabs = [
    { id: 'team-collaboration', name: 'Team Collaboration', icon: <Users size={18} /> },
    { id: 'document-sharing', name: 'Document Sharing', icon: <FileText size={18} /> },
    { id: 'communication', name: 'Communication', icon: <MessageSquare size={18} /> },
    { id: 'project-collaboration', name: 'Project Collaboration', icon: <Calendar size={18} /> }
  ];

  const handleAction = (action, data) => {
    setModalData({ action, data });
    setShowModal(true);
  };

  const handleConfirm = () => {
    setShowModal(false);
    setModalData(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Online': return <CheckCircle size={16} color="#10b981" />;
      case 'Away': return <Clock size={16} color="#f59e0b" />;
      case 'Offline': return <AlertCircle size={16} color="#6b7280" />;
      default: return <AlertCircle size={16} color="#6b7280" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'Medium': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Low': return { color: '#059669', backgroundColor: '#f0fdf4' };
      default: return { color: '#6b7280', backgroundColor: '#f9fafb' };
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return '#10b981';
    if (progress >= 60) return '#007B8F';
    if (progress >= 40) return '#f59e0b';
    return '#dc2626';
  };

  const renderTeamCollaboration = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total Team Members</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{teamMembers.length}</p>
          </div>
          <Users size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Online Now</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#10b981', margin: 0 }}>{teamMembers.filter(m => m.status === 'Online').length}</p>
          </div>
          <CheckCircle size={32} color="#10b981" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Active Projects</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#007B8F', margin: 0 }}>{projects.length}</p>
          </div>
          <Calendar size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Shared Documents</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>{sharedDocuments.length}</p>
          </div>
          <FileText size={32} color="#8b5cf6" />
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>Team Members</h3>
        </div>
        <div style={{ padding: '24px' }}>
          <DataTable
            data={teamMembers}
            columns={[
              {
                key: 'name',
                label: 'Name',
                render: (item) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#007B8F',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {item.avatar}
                    </div>
                    <div>
                      <p style={{ fontWeight: '500', color: '#1f2937', margin: '0 0 4px 0' }}>{item.name}</p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{item.role}</p>
                    </div>
                  </div>
                )
              },
              {
                key: 'department',
                label: 'Department'
              },
              {
                key: 'status',
                label: 'Status',
                render: (item) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getStatusIcon(item.status)}
                    <span style={{ fontSize: '14px' }}>{item.status}</span>
                  </div>
                )
              },
              {
                key: 'lastActive',
                label: 'Last Active'
              },
              {
                key: 'projects',
                label: 'Projects'
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAction('message', item)}
                      style={{
                        color: '#007B8F',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#005A6B'}
                      onMouseLeave={(e) => e.target.style.color = '#007B8F'}
                    >
                      Message
                    </button>
                    <button
                      onClick={() => handleAction('view-profile', item)}
                      style={{
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#374151'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      View Profile
                    </button>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );

  const renderDocumentSharing = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total Documents</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{sharedDocuments.length}</p>
          </div>
          <FileText size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total Downloads</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#10b981', margin: 0 }}>{sharedDocuments.reduce((sum, doc) => sum + doc.downloads, 0)}</p>
          </div>
          <CheckCircle size={32} color="#10b981" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Public Documents</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#007B8F', margin: 0 }}>{sharedDocuments.filter(doc => doc.accessLevel === 'Public').length}</p>
          </div>
          <Users size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Restricted Documents</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626', margin: 0 }}>{sharedDocuments.filter(doc => doc.accessLevel === 'Restricted').length}</p>
          </div>
          <AlertCircle size={32} color="#dc2626" />
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>Shared Documents</h3>
        </div>
        <div style={{ padding: '24px' }}>
          <DataTable
            data={sharedDocuments}
            columns={[
              {
                key: 'name',
                label: 'Document Name',
                render: (item) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FileText size={16} color="#6b7280" />
                    </div>
                    <div>
                      <p style={{ fontWeight: '500', color: '#1f2937', margin: '0 0 4px 0' }}>{item.name}</p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{item.type} • {item.size}</p>
                    </div>
                  </div>
                )
              },
              {
                key: 'sharedBy',
                label: 'Shared By'
              },
              {
                key: 'sharedDate',
                label: 'Shared Date'
              },
              {
                key: 'accessLevel',
                label: 'Access Level',
                render: (item) => (
                  <span style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '12px',
                    ...(item.accessLevel === 'Public' ? { backgroundColor: '#f0fdf4', color: '#166534' } :
                        item.accessLevel === 'Team' ? { backgroundColor: '#eff6ff', color: '#1e40af' } :
                        { backgroundColor: '#fef2f2', color: '#991b1b' })
                  }}>
                    {item.accessLevel}
                  </span>
                )
              },
              {
                key: 'downloads',
                label: 'Downloads'
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAction('download', item)}
                      style={{
                        color: '#007B8F',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#005A6B'}
                      onMouseLeave={(e) => e.target.style.color = '#007B8F'}
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleAction('share', item)}
                      style={{
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#374151'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      Share
                    </button>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );

  const renderCommunication = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total Messages</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{communications.length}</p>
          </div>
          <MessageSquare size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Unread Messages</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626', margin: 0 }}>{communications.filter(c => c.status === 'Unread').length}</p>
          </div>
          <Bell size={32} color="#dc2626" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>High Priority</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b', margin: 0 }}>{communications.filter(c => c.priority === 'High').length}</p>
          </div>
          <AlertCircle size={32} color="#f59e0b" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Announcements</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>{communications.filter(c => c.type === 'Announcement').length}</p>
          </div>
          <Bell size={32} color="#8b5cf6" />
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>Communication</h3>
        </div>
        <div style={{ padding: '24px' }}>
          <DataTable
            data={communications}
            columns={[
              {
                key: 'type',
                label: 'Type',
                render: (item) => (
                  <span style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '12px',
                    ...(item.type === 'Message' ? { backgroundColor: '#eff6ff', color: '#1e40af' } :
                        item.type === 'Announcement' ? { backgroundColor: '#faf5ff', color: '#7c3aed' } :
                        { backgroundColor: '#f0fdf4', color: '#166534' })
                  }}>
                    {item.type}
                  </span>
                )
              },
              {
                key: 'from',
                label: 'From'
              },
              {
                key: 'to',
                label: 'To'
              },
              {
                key: 'subject',
                label: 'Subject'
              },
              {
                key: 'priority',
                label: 'Priority',
                render: (item) => {
                  const colors = getPriorityColor(item.priority);
                  return (
                    <span style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      fontWeight: '500',
                      borderRadius: '12px',
                      color: colors.color,
                      backgroundColor: colors.backgroundColor
                    }}>
                      {item.priority}
                    </span>
                  );
                }
              },
              {
                key: 'timestamp',
                label: 'Timestamp'
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAction('reply', item)}
                      style={{
                        color: '#007B8F',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#005A6B'}
                      onMouseLeave={(e) => e.target.style.color = '#007B8F'}
                    >
                      Reply
                    </button>
                    <button
                      onClick={() => handleAction('mark-read', item)}
                      style={{
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#374151'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      Mark Read
                    </button>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );

  const renderProjectCollaboration = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Active Projects</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{projects.length}</p>
          </div>
          <Calendar size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total Tasks</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#10b981', margin: 0 }}>{projects.reduce((sum, project) => sum + project.tasks, 0)}</p>
          </div>
          <CheckCircle size={32} color="#10b981" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Completed Tasks</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#007B8F', margin: 0 }}>{projects.reduce((sum, project) => sum + project.completed, 0)}</p>
          </div>
          <CheckCircle size={32} color="#007B8F" />
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Team Members</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>{new Set(projects.flatMap(p => p.team)).size}</p>
          </div>
          <Users size={32} color="#8b5cf6" />
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>Projects</h3>
        </div>
        <div style={{ padding: '24px' }}>
          <DataTable
            data={projects}
            columns={[
              {
                key: 'name',
                label: 'Project Name',
                render: (item) => (
                  <div>
                    <p style={{ fontWeight: '500', color: '#1f2937', margin: '0 0 4px 0' }}>{item.name}</p>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{item.description}</p>
                  </div>
                )
              },
              {
                key: 'team',
                label: 'Team',
                render: (item) => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {item.team && Array.isArray(item.team) ? item.team.map((member, index) => (
                      <span key={index} style={{
                        padding: '4px 8px',
                        backgroundColor: '#eff6ff',
                        color: '#1e40af',
                        fontSize: '12px',
                        borderRadius: '12px'
                      }}>
                        {member}
                      </span>
                    )) : <span style={{ color: '#6b7280', fontSize: '14px' }}>No team assigned</span>}
                  </div>
                )
              },
              {
                key: 'progress',
                label: 'Progress',
                render: (item) => (
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                      <span>{item.progress}%</span>
                      <span>{item.completed}/{item.tasks} tasks</span>
                    </div>
                    <div style={{
                      width: '100%',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '4px',
                      height: '8px'
                    }}>
                      <div 
                        style={{
                          height: '8px',
                          borderRadius: '4px',
                          backgroundColor: getProgressColor(item.progress),
                          width: `${item.progress}%`
                        }}
                      ></div>
                    </div>
                  </div>
                )
              },
              {
                key: 'deadline',
                label: 'Deadline'
              },
              {
                key: 'status',
                label: 'Status',
                render: (item) => (
                  <span style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '12px',
                    ...(item.status === 'In Progress' ? { backgroundColor: '#eff6ff', color: '#1e40af' } :
                        item.status === 'Near Completion' ? { backgroundColor: '#f0fdf4', color: '#166534' } :
                        { backgroundColor: '#f9fafb', color: '#374151' })
                  }}>
                    {item.status}
                  </span>
                )
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAction('view-project', item)}
                      style={{
                        color: '#007B8F',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#005A6B'}
                      onMouseLeave={(e) => e.target.style.color = '#007B8F'}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleAction('edit-project', item)}
                      style={{
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#374151'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      Edit
                    </button>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'team-collaboration':
        return renderTeamCollaboration();
      case 'document-sharing':
        return renderDocumentSharing();
      case 'communication':
        return renderCommunication();
      case 'project-collaboration':
        return renderProjectCollaboration();
      default:
        return renderTeamCollaboration();
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Collaboration Hub
        </h1>
        <p className="page-subtitle">
          Team collaboration, document sharing, and communication tools
        </p>
      </div>

      {/* Tabbed Navigation */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #e5e7eb',
          gap: '0'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: activeTab === tab.id ? '#007B8F' : '#6b7280',
                borderBottom: activeTab === tab.id ? '3px solid #007B8F' : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <div style={{
                color: activeTab === tab.id ? '#007B8F' : '#9ca3af'
              }}>
                {tab.icon}
              </div>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        title={modalData?.action ? `Confirm ${modalData.action}` : 'Confirm Action'}
        message={modalData?.data ? `Are you sure you want to ${modalData.action} ${modalData.data.name || modalData.data.subject || 'this item'}?` : 'Are you sure you want to perform this action?'}
      />
    </div>
  );
};

export default CollaborationHub;